/* Como o painel grava: debounce, fila por destino e reversão em falha.
 *
 * Tudo que escreve no banco a partir desta tela passa por aqui. O capítulo
 * que desenha campo só chama `editarProjeto`/`editarIntegrador` e confia que
 * a gravação acontece; quem navega chama `descarregar` antes, porque a
 * próxima tela recalcula do banco. */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  mensagemDoErro,
  useIntegrador,
  useProjetoAtual,
  useSalvarIntegrador,
  useSalvarProjeto,
  type Integrador,
  type Projeto,
} from '@/dados';

/** Os dois destinos de escrita desta tela. */
type Alvo = 'projeto' | 'integrador';
const ALVOS: readonly Alvo[] = ['projeto', 'integrador'];

/** Edições ainda não confirmadas pelo banco, por destino. */
type Rascunho = { projeto: Partial<Projeto>; integrador: Partial<Integrador> };
const VAZIO: Rascunho = { projeto: {}, integrador: {} };

/** Tempo entre a última tecla e a gravação. Mesmo valor da versão anterior. */
const DEBOUNCE_MS = 600;

/** Link que não deixa edição para trás. */
export type NavegarPara = (rota: string) => (evento: React.MouseEvent) => void;

export function useSalvamento() {
  const navegar = useNavigate();

  const consultaIntegrador = useIntegrador();
  const consultaProjeto = useProjetoAtual();
  const salvarProjeto = useSalvarProjeto();
  const salvarIntegrador = useSalvarIntegrador();

  const integradorBanco = consultaIntegrador.data ?? null;
  const projetoBanco = consultaProjeto.data ?? null;
  const projetoId = projetoBanco?.id;

  const [rascunho, setRascunho] = React.useState<Rascunho>(VAZIO);
  const [emVoo, setEmVoo] = React.useState(0);
  // Contador de reversões por destino. Ele entra na `key` dos campos daquele
  // destino: quando uma gravação falha, o campo é REMONTADO e volta a mostrar
  // o que está no banco. Sem isto o modelo reverte e o input em foco continua
  // exibindo o número recusado, que é a pior das duas mentiras.
  const [revisao, setRevisao] = React.useState({ projeto: 0, integrador: 0 });

  // O rascunho também vive num ref porque quem grava é um setTimeout: ler o
  // estado por closure devolveria o valor de dois caracteres atrás.
  const rascunhoRef = React.useRef<Rascunho>(rascunho);
  const relogios = React.useRef<Record<Alvo, ReturnType<typeof setTimeout> | null>>({
    projeto: null,
    integrador: null,
  });
  // Uma fila por destino: nunca há duas requisições do MESMO alvo em voo.
  // Respostas fora de ordem gravariam a edição velha por cima da nova.
  const fila = React.useRef<Record<Alvo, Promise<void>>>({
    projeto: Promise.resolve(),
    integrador: Promise.resolve(),
  });

  // Ponte para os valores que mudam a cada render mas são lidos dentro de
  // temporizadores criados uma vez só.
  const atualizado = React.useRef({ projetoId, salvarProjeto, salvarIntegrador });
  atualizado.current = { projetoId, salvarProjeto, salvarIntegrador };

  function guardar(novo: Rascunho): void {
    rascunhoRef.current = novo;
    setRascunho(novo);
  }

  function editarProjeto(patch: Partial<Projeto>): void {
    guardar({ ...rascunhoRef.current, projeto: { ...rascunhoRef.current.projeto, ...patch } });
    agendar('projeto');
  }

  function editarIntegrador(patch: Partial<Integrador>): void {
    guardar({ ...rascunhoRef.current, integrador: { ...rascunhoRef.current.integrador, ...patch } });
    agendar('integrador');
  }

  /**
   * Tira do rascunho só o que subiu com ESTE valor. Se o usuário digitou de
   * novo enquanto a requisição voava, a tecla mais nova continua pendente e
   * sai no próximo envio. Vale para sucesso e para falha: na falha, o campo
   * volta a espelhar o banco, que é a definição de reverter.
   */
  function assentar(alvo: Alvo, enviado: Record<string, unknown>): void {
    const pendentes = rascunhoRef.current[alvo] as Record<string, unknown>;
    const restante: Record<string, unknown> = {};
    for (const [chave, valor] of Object.entries(pendentes)) {
      if (!(chave in enviado) || !Object.is(valor, enviado[chave])) restante[chave] = valor;
    }
    guardar(
      alvo === 'projeto'
        ? { ...rascunhoRef.current, projeto: restante as Partial<Projeto> }
        : { ...rascunhoRef.current, integrador: restante as Partial<Integrador> },
    );
  }

  async function gravar(alvo: Alvo): Promise<void> {
    const enviado: Record<string, unknown> = { ...rascunhoRef.current[alvo] };
    if (Object.keys(enviado).length === 0) return;

    const { projetoId: id, salvarProjeto: pm, salvarIntegrador: im } = atualizado.current;
    // Sem projeto não há linha para gravar. O veredito já manda cadastrar.
    if (alvo === 'projeto' && !id) return;

    setEmVoo((n) => n + 1);
    try {
      if (alvo === 'projeto') await pm.mutateAsync({ id, ...(enviado as Partial<Projeto>) });
      else await im.mutateAsync(enviado as Partial<Integrador>);
      assentar(alvo, enviado);
    } catch (erro) {
      assentar(alvo, enviado);
      setRevisao((r) =>
        alvo === 'projeto'
          ? { ...r, projeto: r.projeto + 1 }
          : { ...r, integrador: r.integrador + 1 },
      );
      toast.error(mensagemDoErro(erro));
    } finally {
      setEmVoo((n) => n - 1);
    }
  }

  function enfileirar(alvo: Alvo): void {
    fila.current[alvo] = fila.current[alvo].then(() => gravar(alvo));
  }

  function agendar(alvo: Alvo): void {
    const relogio = relogios.current[alvo];
    if (relogio) clearTimeout(relogio);
    relogios.current[alvo] = setTimeout(() => {
      relogios.current[alvo] = null;
      enfileirar(alvo);
    }, DEBOUNCE_MS);
  }

  /** Dispara os envios pendentes agora, sem esperar. Melhor esforço. */
  function empurrar(): void {
    for (const alvo of ALVOS) {
      const relogio = relogios.current[alvo];
      if (relogio) {
        clearTimeout(relogio);
        relogios.current[alvo] = null;
        enfileirar(alvo);
      }
    }
  }

  /**
   * Descarrega TODAS as edições pendentes e espera o banco confirmar.
   *
   * Obrigatório antes de qualquer navegação: a próxima tela (o relatório em
   * especial) recalcula a partir do banco. Sem isto ela imprime número velho,
   * que é exatamente a divergência que esta arquitetura existe para matar.
   */
  async function descarregar(): Promise<void> {
    empurrar();
    await Promise.all([fila.current.projeto, fila.current.integrador]);
  }

  React.useEffect(() => {
    // Saída fora do nosso controle (aba fechada, voltar do navegador) e saída
    // por dentro (a barra lateral desmonta a tela): nos dois o que estiver
    // pendente vai embora antes.
    const aoSair = () => empurrar();
    window.addEventListener('pagehide', aoSair);
    return () => {
      window.removeEventListener('pagehide', aoSair);
      aoSair();
    };
  }, []);

  async function irPara(rota: string): Promise<void> {
    await descarregar();
    navegar(rota);
  }

  /** Link que não deixa edição para trás. Ctrl ou Cmd clique segue o caminho normal. */
  function aoNavegar(rota: string) {
    return (evento: React.MouseEvent) => {
      if (evento.metaKey || evento.ctrlKey || evento.shiftKey || evento.button !== 0) return;
      evento.preventDefault();
      void irPara(rota);
    };
  }

  // Banco + rascunho: o que a tela considera verdade enquanto digita.
  const integrador: Integrador | null = integradorBanco
    ? { ...integradorBanco, ...rascunho.integrador }
    : null;
  const projeto: Projeto | null = projetoBanco ? { ...projetoBanco, ...rascunho.projeto } : null;

  const pendente =
    Object.keys(rascunho.projeto).length > 0 || Object.keys(rascunho.integrador).length > 0;

  const carregando = consultaIntegrador.isPending || consultaProjeto.isPending;
  const semEmpresa = !consultaIntegrador.isPending && !integradorBanco;
  const semProjeto = !consultaProjeto.isPending && !projetoBanco;

  return {
    integradorBanco,
    projetoBanco,
    projetoId,
    integrador,
    projeto,
    pendente,
    emVoo,
    revisao,
    carregando,
    semEmpresa,
    semProjeto,
    editarProjeto,
    editarIntegrador,
    empurrar,
    descarregar,
    irPara,
    aoNavegar,
  };
}
